import { IStoredCalculationSource, type ICalculationResult } from "../../Types/Generated/LogbookModel/StoredCalculation"
import type { Fold, Identified } from "./Connection"

type Calculated = Identified & {
  calculated?: unknown;
  calculatedV2?: unknown;
  calculationVersion?: number;
};

type StoredCalc = Identified & {
  version?: number;
  calculated?: unknown;
  calculatedV2?: unknown;
};

export const calculationProjectionFields = [
  "calculated",
  "calculatedV2",
  "calculationVersion",
] as const;

export function preserveCalculationProjection<T extends Calculated>(next: T, previous?: T): T {
  if (!previous) {
    return next;
  }
  return {
    ...next,
    ...(next.calculated === undefined && previous.calculated !== undefined
      ? { calculated: previous.calculated }
      : {}),
    ...(next.calculatedV2 === undefined && previous.calculatedV2 !== undefined
      ? { calculatedV2: previous.calculatedV2 }
      : {}),
    ...(next.calculationVersion === undefined && previous.calculationVersion !== undefined
      ? { calculationVersion: previous.calculationVersion }
      : {}),
  };
}

function latestCalc(calcs: readonly Identified[]): StoredCalc | undefined {
  let best: StoredCalc | undefined;
  for (const calc of calcs) {
    const candidate = calc as StoredCalc;
    if (
      !best ||
      (candidate.version ?? 0) > (best.version ?? 0) ||
      ((candidate.version ?? 0) === (best.version ?? 0) && candidate.id > best.id)
    ) {
      best = candidate;
    }
  }
  return best;
}

export function calculationFolds(sourceType: IStoredCalculationSource): Readonly<Record<string, Fold>> {
  return {
    calculated: {
      source: "StoredCalculation",
      via: "sourceId",
      where: { sourceType },
      apply: (base, calcs) => {
        const latest = latestCalc(calcs);
        return latest && latest.calculated !== undefined
          ? { ...base, calculated: latest.calculated, calculationVersion: latest.version }
          : base;
      },
    },
    calculatedV2: {
      source: "StoredCalculation",
      via: "sourceId",
      where: { sourceType },
      apply: (base, calcs) => {
        const latest = latestCalc(calcs);
        return latest && latest.calculatedV2 !== undefined
          ? { ...base, calculatedV2: latest.calculatedV2 }
          : base;
      },
    },
  };
}

export const notableFolds: Readonly<Record<string, Fold>> = {
  notes: {
    source: "Note",
    via: "dayId",
    with: ["attachments"],
    apply: (base, notes) => ({ ...base, notes }),
  },
};

export const tripNotableFolds: Readonly<Record<string, Fold>> = {
  notes: {
    source: "Note",
    via: "tripId",
    where: { dayId: undefined },
    with: ["attachments"],
    apply: (base, notes) => ({ ...base, notes }),
  },
};

export const cruiseNotableFolds: Readonly<Record<string, Fold>> = {
  notes: {
    source: "Note",
    via: "cruiseId",
    where: { tripId: undefined, dayId: undefined },
    with: ["attachments"],
    apply: (base, notes) => ({ ...base, notes }),
  },
};

export const noteFolds: Readonly<Record<string, Fold>> = {
  attachments: {
    source: "NoteAttachment",
    via: "noteId",
    apply: (base, attachments) => ({ ...base, attachments }),
  },
};

export function applyCalculationResults<T extends Identified>(
  rows: readonly T[],
  calculations: readonly ICalculationResult<unknown>[] = [],
  calculationsV2: readonly ICalculationResult<unknown>[] = [],
): T[] {
  return rows.map((row) => {
    const calculated = calculations.find((result) => result.id === row.id);
    const calculatedV2 = calculationsV2.find((result) => result.id === row.id);
    if (!calculated && !calculatedV2) {
      return row;
    }
    return {
      ...row,
      ...(calculated ? {
        calculated: calculated.values,
        calculationVersion: calculated.version,
      } : {}),
      ...(calculatedV2 ? { calculatedV2: calculatedV2.values } : {}),
    } as T & Calculated;
  });
}
